import { Component, ElementRef, OnInit, OnDestroy, QueryList, ViewChildren, ChangeDetectionStrategy } from "@angular/core";
import { BehaviorSubject, catchError, combineLatest, EMPTY, map, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { RemarkData } from "../models/remarkDataModel";
import { Store } from "@ngrx/store";
import { Actions, ofType } from "@ngrx/effects";
import { deleteRemarksData, fetchRemarksData, fetchRemarksError, deleteRemarksError, deleteRemarksSuccess, updateRemarksData } from "../store/admin/admin.action";
import { selectRemarksData } from "../store/admin/admin.selector";
import { MatDialog } from "@angular/material/dialog";
import { DeleteModalComponent } from "./deleteModal.component";
import { AuthService } from "../service/auth.service";
import { userReadWriteGroups } from "../../assets/user-groups";
import { NoopScrollStrategy } from "@angular/cdk/overlay";
import { NotificationService } from "../service/notification.service";

type SortKey = 'suffixLetter' | 'name' | 'updatedAt' | 'active'

@Component({
    selector: "app-admin",
    templateUrl: "./admin.component.html",
    styleUrl: "./admin.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AdminComponent implements OnInit, OnDestroy {
    @ViewChildren('deleteModal') deleteModals: QueryList<ElementRef> | undefined;

    userNameAndEmail: string = ""
    sortKey$ = new BehaviorSubject<SortKey>("updatedAt");
    searchQuery$ = new BehaviorSubject<string>("");
    remarksData$ = new Observable<RemarkData[]>();
    filteredRemarksData$ = new Observable<RemarkData[]>();
    filteredRemarksData: RemarkData[] = [];
    isLoading = false;
    private destroy$ = new Subject<void>();

    constructor(
        private authService: AuthService,
        private store: Store,
        private actions$: Actions,
        private dialog: MatDialog,
        private notificationService: NotificationService
    ) {
        this.remarksData$ = this.store.select(selectRemarksData);
    }

    ngOnInit(): void {
        try {
            this.authService.hasReadWriteGroups(userReadWriteGroups.DLI);
            this.userNameAndEmail = this.authService.getUserNameAndEmail();
            this.store.dispatch(fetchRemarksData());
            
            this.filteredRemarksData$ = combineLatest([
                this.searchQuery$,
                this.remarksData$,
                this.sortKey$,
            ]).pipe(
                map(([searchQuery, remarksData]) => {
                  const filteredRemarksData = remarksData.filter(remark => Object.values(remark)
                      .some(value => String(value).toLowerCase().includes(searchQuery)));

                  return searchQuery.trim().length ? this.sortBySortOption(filteredRemarksData) : this.sortBySortOption(remarksData);
                }),
                catchError((error) => {
                    console.error('Error in filtered remarks data:', error);
                    this.notificationService.showError('Kunde inte ladda anmärkningar');
                    return EMPTY;
                })
            );
            
            this.setupSubscriptions();
        } catch (error) {
            console.error('Error during admin component initialization:', error);
            this.notificationService.showError('Ett fel uppstod vid laddning av admin-sidan');
        }
    }

    private setupSubscriptions(): void {
        this.filteredRemarksData$
            .pipe(takeUntil(this.destroy$))
            .subscribe((remarksData) => {
                this.filteredRemarksData = remarksData;
            });

        this.actions$.pipe(ofType(fetchRemarksData), takeUntil(this.destroy$)).subscribe(() => {
            this.isLoading = true;
        });

        this.actions$.pipe(ofType(updateRemarksData), takeUntil(this.destroy$)).subscribe(() => {
            this.isLoading = false;
        });

        this.actions$.pipe(ofType(fetchRemarksError), takeUntil(this.destroy$)).subscribe((action) => {
            console.error('Fetch remarks error received:', action.error);
            this.isLoading = false;
            this.notificationService.showError('Kunde inte ladda anmärkningar');
        });

        this.actions$.pipe(ofType(deleteRemarksError), takeUntil(this.destroy$)).subscribe((action) => {
            console.error('Delete remarks error received:', action.error);
            this.notificationService.showError('Kunde inte ta bort anmärkning');
        });

        this.actions$.pipe(ofType(deleteRemarksSuccess), takeUntil(this.destroy$)).subscribe((action) => {
            console.log('Delete remarks success received for id:', action.id);
            this.notificationService.showSuccess('Anmärkning har tagits bort');
        });
    }

    sortBySortOption(remarksData: RemarkData[]) {
        const currentSortKey = this.sortKey$.value;
        if (['updatedAt', 'active'].includes(currentSortKey)) {
            return remarksData.slice().sort((_a, _b) => {
                const a = _a[currentSortKey];
                const b = _b[currentSortKey];
                return a === b ? 0 : a > b ? -1 : 1;
            });
        } else {
            return remarksData.slice().sort((_a, _b) => {
                const a = _a[currentSortKey];
                const b = _b[currentSortKey];
                return a === b ? 0 : a > b ? 1 : -1;
            });
        }
    }

    logout() {
        this.authService.logout();
    }

    get sortKey(): SortKey {
        return this.sortKey$.value;
    }

    setSortKey(sortKey: string): void {
        this.sortKey$.next(sortKey as SortKey);
    }

    setSearchQuery(value: string) {
        this.searchQuery$.next(value);
    }

    deleteThisRemark(id: string) {
        this.store.dispatch(deleteRemarksData({ id }));
    }

    openDeleteModal(id: string): void {
        const activeRemark = this.filteredRemarksData.find((remark) => remark.id === id);
        
        if (!activeRemark) {
            console.error('Remark not found for deletion:', id);
            this.notificationService.showError('Anmärkning kunde inte hittas');
            return;
        }

        const dialogRef = this.dialog.open(DeleteModalComponent, {
            width: '400px',
            data: {
                title: 'Bekräfta borttagning',
                message: `Är du säker på att du vill ta bort anmärkning: ${activeRemark.name}?`,
                noLabel: 'Avbryt',
                yesLabel: 'Ta bort',
            },
            scrollStrategy: new NoopScrollStrategy(),
        });

        dialogRef.afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result) {
                    this.deleteThisRemark(id);
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
